import useTranslation from 'next-translate/useTranslation'
import React, { useEffect, useState } from 'react'
import { CheckCpfResult } from '@arland-bmnext/api-data'
import { useForm } from 'react-hook-form'
import { isNullOrEmpty } from '../../../util/string'
import { getCpfData } from '../../../lib/account'
import { CpfInput } from '../../forms/elements/CpfInput'
import { CpfValidator } from '../../../util/validators'
import { ContainedButton } from '../../core/Button'

export const CpfCheckForm = ({
  onCpfCheckCompleted,
  title,
  description,
}: {
  onCpfCheckCompleted: (cpfRequestData: CheckCpfResult) => void
  title?: string
  description?: string
}) => {
  const { t } = useTranslation('register')

  const [cpfRequestState, setCpfRequestState] = useState<'checking' | 'valid' | 'invalid' | undefined>(undefined)
  const [cpfRequestData, setCpfRequestData] = useState<CheckCpfResult>(undefined)
  const [cpfIsUnique, setCpfIsUnique] = useState(undefined)

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    getFieldState,
    formState: { isValid, errors },
  } = useForm<any>({
    mode: 'onChange',
    shouldFocusError: true,
  })

  useEffect(() => {
    if (isValid && cpfRequestData == null) {
      requestCpfData()
      return
    }

    if (!isValid) {
      setCpfRequestData(undefined)
      setCpfRequestState(undefined)
      setCpfIsUnique(undefined)
    }
  }, [isValid])

  const requestCpfData = async () => {
    if (getFieldState('cpf').invalid) return

    const cpf = getValues('cpf')
    if (!isNullOrEmpty(cpf)) {
      setCpfRequestState('checking')
      const data = await getCpfData(cpf)
      if (data?.nationalIdentity?.isValid && data?.isCpfUnique) {
        setCpfRequestData(data)
        setCpfRequestState('valid')
      } else {
        setCpfRequestState('invalid')
      }
      setCpfIsUnique(data.isCpfUnique ?? false)
    }
  }

  const onSubmit = () => {
    if (cpfIsUnique === false) return
    onCpfCheckCompleted(cpfRequestData)
  }

  return (
    <form className="form-builder" onSubmit={handleSubmit(onSubmit)}>
      {title && <span className={'text-xl font-bold'}>{title}</span>}
      {description && <div dangerouslySetInnerHTML={{ __html: description }} />}

      <div className={`form-fields space-y-6`}>
        <CpfInput
          label={'CPF'}
          property={'cpf'}
          required={true}
          register={register}
          setValue={setValue}
          getValues={getValues}
          validators={CpfValidator(t)}
          errors={errors['cpf'] as any}
          cpfValidationState={cpfRequestState}
          disabled={cpfRequestState === 'valid'}
        />
      </div>

      <div className="form-builder-controls flex justify-end space-x-2 pt-8">
        <ContainedButton
          type="submit"
          className="form-builder-control-submit"
          backgroundColor={cpfIsUnique === false ? 'bg-red-500' : 'bg-primary'}
          textColor={cpfIsUnique === false ? 'text-white' : 'text-primary-contrast'}
          borderColor="border-none"
          disabled={cpfRequestState !== 'valid' || cpfIsUnique === false}
          rounded
          fullWidth
        >
          {cpfIsUnique === false ? t('common:Validation.nationalIdTaken') : t('common:Common.next')}
        </ContainedButton>
      </div>
    </form>
  )
}
